import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star, Zap, Shield, Globe, Users, Award, Rocket, Heart } from 'lucide-react';

const OutstandingFeatures = [
    {
        icon: <Star className="w-12 h-12 text-yellow-500" />,
        heading: "Tailored Support",
        para: "The competency mapping session with an industry expert ensures that every user's strength and area for improvement are clearly identified at the outset. This tailored approach makes users feel seen and supported, increasing engagement and motivation throughout the program.",
    },
    {
        icon: <Zap className="w-12 h-12 text-blue-500" />,
        heading: "Accelerated Skill Development",
        para: "One-on-one IT upskilling in high-demand domains provides hands-on, personalized learning. Users gain practical, job-ready skills quickly, which boosts confidence and readiness for real-world roles. This focused mentorship helps users stay ahead in the competitive tech industry.",
    },
    {
        icon: <Shield className="w-12 h-12 text-green-500" />,
        heading: "Enhances Professionalism and Communication",
        para: "Professional behavioral grooming empowers users with strong communication skills and professional demeanor. This not only helps them stand out in interviews but also ensures smoother workplace integration and long-term career growth.",
    },
    {
        icon: <Globe className="w-12 h-12 text-purple-500" />,
        heading: "Confidence in Interview Settings",
        para: "The comprehensive interview preparation kit—including tailored prep sessions, hundreds of essential questions, and multiple mock interviews—directly addresses interview anxiety. Users feel better prepared and more confident, leading to improved performance and higher chances of success in actual interviews.",
    },
    {
        icon: <Users className="w-12 h-12 text-indigo-500" />,
        heading: "Stronger Job Market Positioning",
        para: "Resume reviews by technical specialists ensure users' profiles are optimized for Applicant Tracking Systems (ATS) and aligned with real job descriptions. This increases their visibility to recruiters and improves their chances of being shortlisted for interviews.",
    },
    {
        icon: <Award className="w-12 h-12 text-orange-500" />,
        heading: "Real-World Readiness",
        para: "End-to-end project experience simulates actual workplace challenges, allowing users to apply their skills in practical scenarios. This bridges the gap between training and employment, making users genuinely job-ready and more attractive to employers.",
    },
    {
        icon: <Rocket className="w-12 h-12 text-red-500" />,
        heading: "Strategic and Stress-Free Job Search",
        para: "Full-time job searching assistance, including hundreds to thousands of job submissions, direct applications, and personalized outreach, removes the burden of job hunting from users. This systematic, aggressive approach maximizes exposure and interview opportunities, reducing stress and saving time.",
    },
    {
        icon: <Heart className="w-12 h-12 text-pink-500" />,
        heading: "Continuous Feedback and Growth",
        para: "Technical mock interviews (especially in the Switch Plan) provide users with realistic practice and actionable feedback. This iterative process ensures continuous improvement, so users approach each real interview with greater clarity and confidence.",
    },
];

export default function AdvancedSlidingCards() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlay, setIsAutoPlay] = useState(true);
    const [hoveredCard, setHoveredCard] = useState(null);
    const [cardsToShow, setCardsToShow] = useState(3);

    // Update cards to show based on screen size
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                setCardsToShow(3);
            } else if (window.innerWidth >= 768) {
                setCardsToShow(2);
            } else {
                setCardsToShow(1);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-play functionality
    useEffect(() => {
        if (!isAutoPlay) return;

        const maxIndex = Math.max(0, OutstandingFeatures.length - cardsToShow);
        
        const interval = setInterval(() => {
            setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
        }, 4000);

        return () => clearInterval(interval);
    }, [isAutoPlay, cardsToShow]);

    const maxIndex = Math.max(0, OutstandingFeatures.length - cardsToShow);

    const nextSlide = () => {
        setCurrentIndex(prev => prev >= maxIndex ? 0 : prev + 1);
    };

    const prevSlide = () => {
        setCurrentIndex(prev => prev <= 0 ? maxIndex : prev - 1);
    };

    const goToSlide = (index) => {
        setCurrentIndex(Math.min(index, maxIndex));
    };

    return (
        <div className="relative py-16 bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0 opacity-20 pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-r from-pink-400 to-red-500 rounded-full blur-3xl"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-6">
                        Outstanding Features
                    </h1>
                    <div className="flex justify-center mb-4">
                        <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 w-32 rounded-full"></div>
                    </div>
                    <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
                        Discover the exceptional features that make us the industry leader
                    </p>
                </div>

                {/* Main Slider Container */}
                <div
                    className="relative"
                    onMouseEnter={() => setIsAutoPlay(false)}
                    onMouseLeave={() => setIsAutoPlay(true)}
                >
                    {/* Cards Container */}
                    <div className="overflow-hidden rounded-2xl">
                        <div
                            className="flex transition-transform duration-700 ease-in-out"
                            style={{
                                transform: `translateX(-${(currentIndex * 100) / cardsToShow}%)`,
                            }}
                        >
                            {OutstandingFeatures.map((feature, index) => (
                                <div
                                    key={index}
                                    className={`flex-shrink-0 px-3 py-6 ${
                                        cardsToShow === 1 ? 'w-full' : 
                                        cardsToShow === 2 ? 'w-1/2' : 'w-1/3'
                                    }`}
                                >
                                    <div
                                        className={`group relative min-h-full bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 cursor-pointer overflow-hidden border-2 border-transparent hover:border-blue-200`}
                                        onMouseEnter={() => setHoveredCard(index)}
                                        onMouseLeave={() => setHoveredCard(null)}
                                    >
                                        {/* Gradient Background */}
                                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                        {/* Card Content */}
                                        <div className="relative p-6 lg:p-8 h-full flex flex-col justify-center items-center text-center">
                                            {/* Icon with Animation */}
                                            <div className="mb-6 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                                                <div className="p-4 bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg group-hover:shadow-xl">
                                                    {feature.icon}
                                                </div>
                                            </div>

                                            {/* Title */}
                                            <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                                                {feature.heading}
                                            </h3>

                                            {/* Description */}
                                            <p className="text-sm lg:text-base text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300 line-clamp-7 text-justify">
                                                {feature.para}
                                            </p>
                                        </div>

                                        {/* Floating Elements */}
                                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full opacity-0 group-hover:opacity-100 transform group-hover:scale-100 scale-0 transition-all duration-500 delay-100"></div>
                                        <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-gradient-to-br from-pink-400 to-red-500 rounded-full opacity-0 group-hover:opacity-100 transform group-hover:scale-100 scale-0 transition-all duration-500 delay-200"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Navigation Arrows */}
                    {maxIndex > 0 && (
                        <>
                            <button
                                onClick={prevSlide}
                                className="absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 lg:p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-10"
                            >
                                <ChevronLeft className="w-5 h-5 lg:w-6 lg:h-6" />
                            </button>

                            <button
                                onClick={nextSlide}
                                className="absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 lg:p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-10"
                            >
                                <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6" />
                            </button>
                        </>
                    )}
                </div>

                {/* Dots Indicator */}
                {maxIndex > 0 && (
                    <div className="flex justify-center mt-12 space-x-2 lg:space-x-3">
                        {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToSlide(index)}
                                className={`transition-all duration-300 rounded-full ${
                                    currentIndex === index
                                        ? 'w-8 lg:w-12 h-3 bg-gradient-to-r from-blue-500 to-purple-500'
                                        : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                                }`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}